import React, { useEffect, useState } from "react";
import { ReactSVG } from "react-svg";
import { useSelector, useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import CustomInput from "../../../../common/CustomInput";
import { breastfeedingEnum, pregnancyEnum, trimesterEnum } from "./data";
import ChartNav from "../../../../common/ChartNav";
import Medications from "../../medications/medications";
import Steps from "../../Steps";
import {
  setClinicalServiceSteps,
  updateClinicalService,
  updateMedicationReview,
} from "../../../../../redux/modules/clinical-services/actions";
import Button from "../../../../common/Button";
import { ClinicalServiceTypes, getValidStep } from "../../../text/data";
import EndServiceModel from "../../../../common/EndServiceModel";
import { AlertConfirmationTypes, MinorAilmentStep } from "../../../../../helpers/constant";
import { calculateAge, calculateAgeYear, scrollY } from "../../../../../helpers/functions";
import SubNavigation from "../../common/sub-navigation";

const SpecialPopulations = () => {
  //State Variables
  const { clinicalServiceObj, clinicalServiceName, clinicalServiceSteps } =
    useSelector((state) => state.clinicalService);

  const { patient } = useSelector((state) => state.patients);
  const { currentStep, currentSubStep } =
    clinicalServiceObj.initialAssessment.initialAssessmentForm;
  const { id, serviceId } = useParams();
  const dispatch = useDispatch();

  //Local Variables
  const [pregnant, setPregnant] = useState(null);
  const [breastfeeding, setBreastfeeding] = useState(null);
  const [trimester, setTrimester] = useState(null);
  const [showBreastfeeding, setShowBreastfeeding] = useState(false);
  const [expectancyDate, setExpectancyDate] = useState(null);
  const [infantDOB, setinfantDOB] = useState(null);
  const [openPregnancyAlert, setOpenPregnancyAlert] = useState(false);

  const [completeSubStep, setCompleteSubStep] = useState(false);
  const { showReferral } =
    clinicalServiceObj.initialAssessment.initialAssessmentForm;
  const [showRef, setShowRef] = useState(showReferral ?? false);
  const [clickedEndService, setClickedEndService] = useState(false);
  const [alertConfirmation, setAlertConfirmation] = useState(0);
  const [alertExplaination, setAlertExplaination] = useState('');

  useEffect(() => {
    if (clickedEndService) {
      onNext();
      setClickedEndService(false);
    }
  }, [clinicalServiceSteps, clickedEndService]);

  useEffect(() => {
    if (patient?.dateOfBirth) {
      if (calculateAgeYear(patient.dateOfBirth) > 90) onNext();
    }
  }, [patient]);

  //state management
  useEffect(() => {
    setPregnant(
      clinicalServiceObj.initialAssessment.initialAssessmentForm.preAssessment
        .pregnancyIndicator
    );
    setBreastfeeding(
      clinicalServiceObj.initialAssessment.initialAssessmentForm.preAssessment
        .breastFeedingIndicator
    );
    setinfantDOB(
      clinicalServiceObj.initialAssessment.initialAssessmentForm.preAssessment
        .infantDOB
    );
    setTrimester(
      clinicalServiceObj.initialAssessment.initialAssessmentForm.preAssessment
        .pregnancyTrimester
    );
    setExpectancyDate(
      clinicalServiceObj.initialAssessment.initialAssessmentForm.preAssessment
        .expectancyDate
    );
    if (
      clinicalServiceObj.initialAssessment.initialAssessmentForm.preAssessment
        .breastFeedingIndicator !== null
    ) {
      setShowBreastfeeding(true);
    }
  }, [clinicalServiceObj]);

  useEffect(() => {
    if (pregnant) {
      setShowBreastfeeding(true);
    } else {
      setShowBreastfeeding(false);
    }
  }, [pregnant]);

  //Move Back and Forward
  const onNext = (oldAge = false) => {
    const requiresPregnancyAlertResolution =
      clinicalServiceName === ClinicalServiceTypes.AllergicRhinitis &&
      pregnant === "Yes" &&
      trimester &&
      expectancyDate &&
      alertConfirmation !== AlertConfirmationTypes.EndService;

    if (requiresPregnancyAlertResolution) {
      setOpenPregnancyAlert(true);
      return;
    }

    const { nextStep, nextSubStep } = getValidStep(
      clinicalServiceSteps,
      currentStep,
      currentSubStep,
      1
    );
    const updatedClinicalServiceObj = {
      preAssessment: {
        pregnancyIndicator: pregnant,
        pregnancyTrimester: trimester,
        breastFeedingIndicator: breastfeeding,
        infantDOB: infantDOB,
        expectancyDate: expectancyDate,
        pregnancyAlertConfirmationType: alertConfirmation,
        pregnancyAlertExplaination: alertExplaination
      },
      currentStep: nextStep,
      currentSubStep: nextSubStep,
      showReferral: showRef, 
      serviceSteps: clinicalServiceSteps
    };

    if (clinicalServiceName === ClinicalServiceTypes.AllergicRhinitis) {
      dispatch(updateClinicalService(serviceId, updatedClinicalServiceObj));
    } else if (clinicalServiceName === ClinicalServiceTypes.MedicationReview) {
      dispatch(updateMedicationReview(serviceId, updatedClinicalServiceObj));
    }
  };

  const onPrevious = () => {
    resetComponent();
    const { nextStep, nextSubStep } = getValidStep(
      clinicalServiceSteps,
      currentStep,
      currentSubStep,
      -1
    );
    const updatedSteps = { currentStep: nextStep, currentSubStep: nextSubStep };

    if (clinicalServiceName === ClinicalServiceTypes.AllergicRhinitis) {
      dispatch(updateClinicalService(serviceId, updatedSteps));
    } else if (clinicalServiceName === ClinicalServiceTypes.MedicationReview) {
      dispatch(updateMedicationReview(serviceId, updatedSteps));
    }
  };

  const handleEndService = async (disable = false) => {
    let existingStep = currentStep;
    const updatedServiceSteps = clinicalServiceSteps.map((step, i) => ({
      ...step,
      //disable all the substeps that we are skipping, from next step till documentation step.
      subSteps: step.subSteps.map((subStep) => {
        if (i + 1 > existingStep && i + 1 < MinorAilmentStep.Documentation) {
          return { ...subStep, show: !disable };
        } else return subStep;
      }),
    }));
 
    await dispatch(setClinicalServiceSteps(updatedServiceSteps));
  };

  const resetComponent = () => {
    setTrimester(null);
    setPregnant(null);
    setBreastfeeding(null);
    setExpectancyDate(null);
    setShowBreastfeeding(false);
    setCompleteSubStep(false);
  };

  useEffect(() => {
    if (pregnant === "Yes" && trimester && expectancyDate) {
      if (clinicalServiceName === ClinicalServiceTypes.AllergicRhinitis) {
        setOpenPregnancyAlert(true);
      }
    }
  }, [pregnant, trimester, expectancyDate]);

  useEffect(() => {
    if (
      (pregnant === "No" || pregnant === "Does not apply") &&
      (breastfeeding === "No" || breastfeeding === "Does not apply")
    ) {
      setCompleteSubStep(true);
    } else if (
      (pregnant === "No" || pregnant === "Does not apply") &&
      breastfeeding === "Yes" &&
      infantDOB
    ) {
      setCompleteSubStep(true);
    } else if (
      pregnant === "Yes" &&
      trimester &&
      expectancyDate &&
      (breastfeeding === "No" || breastfeeding === "Does not apply")
    ) {
      setCompleteSubStep(true);
    } else if (
      pregnant === "Yes" &&
      trimester &&
      expectancyDate &&
      breastfeeding === "Yes" &&
      infantDOB
    ) {
      setCompleteSubStep(true);
    } else {
      setCompleteSubStep(false);
    }
  }, [pregnant, breastfeeding, infantDOB, trimester, expectancyDate]);

  const handleSetPregnant = (value) => {
    setPregnant(value);
    if (value === "No") {
      setTrimester(null);
      setExpectancyDate(null);
    } else {
      setTimeout(() => {
        scrollY("pregCard");
      }, 300);
    }
  };

  const infantAgeText = infantDOB ? calculateAge(infantDOB) : "";

  return (
    <div className="flex flex-col flex-1 gap-4 bg-white">
      <div className="flex flex-col flex-1">
        <div className="flex flex-1 gap-4">
          <ChartNav />
          <Medications />
          <div className="flex flex-1 w-full h-full">
            <div className="w-full gap-5 h-full">
              <div className="flex items-center justify-between mt-0.5 mb-0.5">
                <h4 className="text-2xl font-bold text-teal-600">
                  Special Populations
                </h4>
                <SubNavigation />
              </div>
              <div className="border-b-[7px] border-gray-20 mb-[20px]"></div>
              <div className="flex flex-row gap-6">
                <div className={`flex `}>
                  <div className="h-[595px] w-[480px] rounded-[4px] overflow-hidden border border-gray-100 flex flex-col">
                    <div
                      className="items-center justify-start flex flex-row px-5 py-4 bg-cover bg-center"
                      style={{
                        backgroundImage: `url(/icons/texture-background.png)`,
                      }}
                    >
                      <ReactSVG
                        src={"/icons/pregnancy-new.svg"}
                        className={`w-6 pr-14`}
                      />
                      <h3 className="text-xl font-bold text-teal-600 ">
                        Pregnancy
                      </h3>
                    </div>
                    <div
                      id="pregCard"
                      className="flex flex-col gap-5 p-5 overflow-y-auto overflow-x-hidden min-h-[465px]"
                    >
                      <div className="flex flex-col gap-4">
                        <p className="text-lg font-semibold text-gray-800">
                          {pregnancyEnum.question}
                        </p>
                        <div className="flex flex-col gap-2">
                          {pregnancyEnum.options.map((option) => {
                            return (
                              <button
                                key={option.value}
                                onClick={() => handleSetPregnant(option.value)}
                                className={`"font-semibold text-body-md p-3 rounded-[4px] border-[1px]
                                  text-gray-800 hover:text-gray-800 hover:bg-gray-20 hover:border-gray-600" ${
                                    pregnant === option.value
                                      ? "bg-gray-20 border-gray-600"
                                      : "bg-white border-gray-100"
                                  }`}
                              >
                                <p className="font-semibold text-body-md flex justify-start items-start">
                                  {option.label}
                                </p>
                              </button>
                            );
                          })}
                        </div>
                        {pregnant === "Yes" && (
                          <>
                            <div className="border-b border-gray-100"></div>

                            <p className="text-lg font-semibold text-gray-800">
                              {trimesterEnum.question}
                            </p>
                            <div className="flex flex-col gap-2">
                              {trimesterEnum.options.map((option) => {
                                return (
                                  <button
                                    key={option.value}
                                    onClick={() => setTrimester(option.value)}
                                    className={`"font-semibold text-body-md p-3 rounded-[4px] border-[1px]
                                    text-gray-800 hover:text-gray-800 hover:bg-gray-20 hover:border-gray-600" ${
                                      trimester === option.value
                                        ? "bg-gray-20 border-gray-600"
                                        : "bg-white border-gray-100"
                                    }`}
                                  >
                                    <p className="font-semibold text-body-md flex justify-start items-start">
                                      {option.label}
                                    </p>
                                  </button>
                                );
                              })}
                            </div>
                            <div className="border-b border-gray-100"></div>
                            <p className="text-lg font-semibold text-gray-800">
                              Expectancy date
                            </p>
                            <div className="grid grid-cols-12 gap-2">
                              <CustomInput
                                name="expectancyDate"
                                className="col-span-12"
                                type="date"
                                showOptionalLabel="Expectancy Date"
                                value={expectancyDate}
                                required
                                onChange={(value) => setExpectancyDate(value)}
                              />
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                {true && (
                  <>
                    <div className={`flex`}>
                      <div className="h-[595px] w-[480px] rounded-[4px] overflow-hidden border border-gray-100 flex flex-col">
                        <div
                          className="items-center justify-start flex flex-row px-5 py-4 bg-cover bg-center"
                          style={{
                            backgroundImage: `url(/icons/texture-background.png)`,
                          }}
                        >
                          <ReactSVG
                            src={"/icons/breastfeeding.svg"}
                            className={`w-6 pr-14`}
                          />
                          <h3 className="text-xl font-bold text-teal-600 ">
                            Breastfeeding
                          </h3>
                        </div>
                        <div className="flex flex-col gap-5 p-5 overflow-y-auto overflow-x-hidden min-h-[465px]">
                          <div className="flex flex-col gap-4">
                            <p className="text-lg font-semibold text-gray-800">
                              {breastfeedingEnum.question}
                            </p>
                            <div className="flex flex-col gap-2">
                              {breastfeedingEnum.options.map((option) => {
                                return (
                                  <button
                                    key={option.value}
                                    onClick={() =>
                                      setBreastfeeding(option.value)
                                    }
                                    className={`"font-semibold text-body-md p-3 rounded-[4px] border-[1px]
                                    text-gray-800 hover:text-gray-800 hover:bg-gray-20 hover:border-gray-600" ${
                                      breastfeeding === option.value
                                        ? "bg-gray-20 border-gray-600"
                                        : "bg-white border-gray-100"
                                    }`}
                                  >
                                    <p className="font-semibold text-body-md flex justify-start items-start">
                                      {option.label}
                                    </p>
                                  </button>
                                );
                              })}
                            </div>
                            <div>
                              {breastfeeding != null &&
                                breastfeeding == "Yes" && (
                                  <div>
                                    <div className="border-b border-gray-100 mb-2"></div>
                                    <p className="text-lg font-semibold text-gray-800">
                                      Infant date of birth
                                    </p>
                                    <div className="grid grid-cols-12 gap-2">
                                      <CustomInput
                                        name="infantDOB"
                                        className="col-span-12"
                                        type="date"
                                        showOptionalLabel="Infant date of birth"
                                        value={infantDOB}
                                        disableFuture={true}
                                        onChange={(value) =>
                                          setinfantDOB(value)
                                        }
                                        required
                                      />
                                      {infantAgeText ? (
                                        <p className="col-span-12 text-sm text-gray-600">
                                          Infant Age: {infantAgeText}
                                        </p>
                                      ) : null}
                                    </div>
                                  </div>
                                )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <EndServiceModel
        open={openPregnancyAlert}
        showContinueWithNoRef={false}
        showContinueAndRef={false}
        noContinueButton={true}
        title="Pregnancy Alert"
        content={() => (
          <div className="flex flex-col gap-4">
            <div className="text-body-md font-semibold">
              Risk of Hormonal Rhinitis may be due to Pregnancy.
            </div>
            <div className="text-body-sm flex flex-col gap-4">
              <p>
                A previous diagnosis of Allergic Rhinitis still requires
                referral during any trimester of pregnancy.
              </p>
            </div>
          </div>
        )}
        handleClose={() => {
          setOpenPregnancyAlert(false);
          setAlertConfirmation(0);
          setAlertExplaination('');
        }}
        handleContinue={(ref, exp) => {
          setAlertConfirmation(ref ? AlertConfirmationTypes.ContinueWithRefer : AlertConfirmationTypes.ContinueWithNoReferral);
          setAlertExplaination(exp);
          if (ref !== undefined) {
            setShowRef(ref);
          }
          handleEndService(false);
          setOpenPregnancyAlert(false);
        }}
        handleEndService={() => {
          setShowRef(true);
          setAlertConfirmation(AlertConfirmationTypes.EndService);
          setAlertExplaination('');
          setOpenPregnancyAlert(false);
          handleEndService(true);
          setClickedEndService(true);
        }}
      />
      <div className="flex items-center justify-between space-x-4  rounded">
        <div className="w-full ">
          <Steps position="bottom" stepsList={clinicalServiceSteps} />
        </div>
        <div className="flex items-center gap-3 ">
          <Button
            type="primary-outline"
            text="Back"
            className="w-[200px] max-h-[52px] !rounded-[30px]"
            onClick={() => onPrevious()}
          />
          <Button
            type="primary"
            text="Next"
            className={`w-[200px] max-h-[52px] !rounded-[30px] ${
              !completeSubStep && "opacity-40 pointer-events-none"
            }`}
            onClick={() => onNext()}
          />
        </div>
      </div>
    </div>
  );
};

export default SpecialPopulations;
